import React from 'react';
import { BookingPositionState } from '../model/BookingPositionState';
import { dayLabel, formatCount, mainHotel, percentText, whole } from '../BookingRanking.utilities';
import { RankingCard, RankingCardHead } from '../BookingRankingParts';

type Dot = { date: string, position: number, share: number };

const Y_TICKS = [10, 25, 50, 100, 250];

// Feature-local Screen Part (user decision of 2026-10-05): one dot per night of the own hotel with a
// position and a city price share; y = position on a log scale from 10 (top) to 250.
const ScatterSvg: React.FunctionComponent<{ dots: Dot[], width: number, height: number, x0: number, x1: number,
	xRange: [number, number], xTicks: number[], radius: number, className: string }> = ({ dots, width, height, x0, x1, xRange, xTicks, radius, className }) => {
	const top = 30;
	const bottom = height - 40;
	const xm = (v: number) => x0 + (v - xRange[0]) / (xRange[1] - xRange[0]) * (x1 - x0);
	const ym = (p: number) => top + Math.log10(Math.max(p, 10) / 10) / Math.log10(25) * (bottom - top);
	return <svg className={className} viewBox={`0 0 ${width} ${height}`} width="100%" role="img"
		aria-label="Position against the city price, one dot per night">
		{Y_TICKS.map((t) => <g key={`y${t}`}>
			<line x1={x0} x2={x1} y1={ym(t)} y2={ym(t)} className="stroke-gray-100" />
			<text x={x0 - 8} y={ym(t) + 4} textAnchor="end" className="fill-gray-500 text-xs">{t}</text>
		</g>)}
		{xTicks.map((v) => <g key={`x${v}`}>
			<line x1={xm(v)} x2={xm(v)} y1={top} y2={bottom} className="stroke-gray-100" />
			<text x={xm(v)} y={bottom + 16} textAnchor="middle" className="fill-gray-500 text-xs">{v}%</text>
		</g>)}
		<text x={(x0 + x1) / 2} y={height - 4} textAnchor="middle" className="fill-gray-600 text-xs">Dearer than … % of the city</text>
		<text x={x0 - 30} y={top - 14} className="fill-gray-600 text-xs">Position</text>
		{dots.map((d) => <circle key={d.date} cx={xm(d.share)} cy={ym(d.position)} r={radius} fillOpacity={0.7} strokeWidth={1.2}
			className="fill-blue-600 stroke-white">
			<title>{`${dayLabel(d.date)}: position ${whole(d.position)}, dearer than ${percentText(d.share)}`}</title>
		</circle>)}
	</svg>;
};

// Position vs price: your nights only; the dots go down to the right when a dear night gets a low place.
const PositionPriceScatter: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) => {
	const main = mainHotel(state);
	if (main === undefined) return null;
	const dots: Dot[] = main.cells
		.filter((c) => c.state === 'POSITION' && c.position !== null && c.cityDearerShare !== null)
		.map((c) => ({ date: c.date, position: c.position as number, share: c.cityDearerShare as number }));
	if (dots.length === 0) return null;
	const sold = main.cells.filter((c) => c.state === 'SOLD_OUT').length;
	const lo = Math.min(...dots.map((d) => d.share));
	const hi = Math.max(...dots.map((d) => d.share));
	const narrow = lo >= 22 && hi <= 78;
	const xRange: [number, number] = narrow ? [20, 80] : [0, 100];
	const link = state.priceLink;

	return <RankingCard>
		<RankingCardHead title={`Position vs price in ${state.bookingCity.name}, ${dots.length} nights`}
			subtitle="You only · top of the chart = position 10" />
		<div className="flex flex-wrap gap-6 px-4 py-4 sm:px-5">
			<div className="min-w-0 flex-1 basis-96">
				<ScatterSvg className="hidden sm:block" dots={dots} width={680} height={320} x0={56} x1={664} xRange={xRange}
					xTicks={narrow ? [20, 30, 40, 50, 60, 70, 80] : [0, 20, 40, 60, 80, 100]} radius={5} />
				<ScatterSvg className="block sm:hidden" dots={dots} width={320} height={260} x0={44} x1={296} xRange={xRange}
					xTicks={narrow ? [20, 50, 80] : [0, 50, 100]} radius={4} />
			</div>
			<div className="flex min-w-0 flex-1 basis-64 flex-col gap-2.5 text-sm text-gray-700">
				<p>Each dot is one night when you had a position ({dots.length} of {state.nights.length} nights). The {sold} sold-out nights have no dot.</p>
				<p>The dots go down to the right: on nights when your price was dear against the city, Booking.com showed you lower.</p>
				{link !== null && <p className="text-gray-600">
					In {formatCount(link.pairsDearerLower)} of {formatCount(link.pairsUntied)} pairs of nights, the dearer night had the lower place.
					This describes your nights. It does not prove that the price causes the place.
				</p>}
			</div>
		</div>
	</RankingCard>;
};

export default PositionPriceScatter;
